import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Divider,
  Chip,
  Tooltip,
  Collapse,
  Button as MuiButton,
} from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import Code from "@mui/icons-material/Code";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AgentDocumentsSection from "../Agents/AgentDocumentsSection";
import { useS3Upload } from "../../hooks/useS3Upload";
import { useAgents } from "../../hooks/useAgents";
import { getVariablesByGroup } from "./chatbotContextVariables";
import {
  defaultFaqIaConfig,
  defaultAgenteIaConfig,
  MAX_CHATBOT_RAG_DOCS,
  MAX_CHATBOT_RAG_URLS,
  RESPOSTA_MODO_FAQ_IA,
  RESPOSTA_MODO_TEXTO,
  RESPOSTA_MODO_AGENTE_IA,
  ENVIO_RESPOSTA_DIRETO,
  ENVIO_RESPOSTA_PERSONALIZADO,
  MODE_AGENT_PREFIX,
  modeSelectValue,
} from "../../utils/chatbotSlotFaq";

const ACCEPT_DOCS = ".pdf,.docx,.xls,.xlsx";

function ContextVariableChips({ onInsert, extraVars = [], t }) {
  const [open, setOpen] = useState(false);
  const { byGroup, groupLabels } = getVariablesByGroup();
  const extraGroup = extraVars.length ? { agente: extraVars } : {};
  const mergedGroups = { ...byGroup, ...extraGroup };
  const mergedLabels = { ...groupLabels, agente: "Agente de IA" };

  return (
    <Box sx={{ mb: 1 }}>
      <MuiButton
        size="small"
        startIcon={<Code />}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        onClick={() => setOpen((v) => !v)}
        sx={{ textTransform: "none" }}
      >
        {t("chatbotConfigPage.chatbotSlotFaq.variablesBtn", { defaultValue: "Variáveis de contexto" })}
      </MuiButton>
      <Collapse in={open}>
        <Box sx={{ mt: 1, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
          {Object.entries(mergedGroups).map(([groupKey, vars]) => (
            <Box key={groupKey} sx={{ mb: 1.5 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {mergedLabels[groupKey] || groupKey}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {vars.map((v) => (
                  <Tooltip key={v.name} title={`${v.label} — ${v.name}`}>
                    <Chip size="small" label={v.label} onClick={() => onInsert?.(v.name)} sx={{ fontSize: "0.75rem" }} />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function buildDocumentsPrefix(userId) {
  const d = new Date();
  return `workspace-chatbot/ura/${userId || "default"}/knowledge/${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const ChatbotSlotFaqSection = forwardRef(function ChatbotSlotFaqSection(
  {
    respostaModo = RESPOSTA_MODO_TEXTO,
    faqIa,
    agenteIa,
    slotKey = null,
    onRespostaModoChange,
    onFaqIaChange,
    onAgenteIaChange,
    readOnly = false,
    chatbotUserId = null,
    isExcecao = false,
    t = (k, d) => d?.defaultValue || k,
  },
  ref
) {
  const [mode, setMode] = useState(RESPOSTA_MODO_TEXTO);
  const [pergunta, setPergunta] = useState("");
  const [envioResposta, setEnvioResposta] = useState(ENVIO_RESPOSTA_DIRETO);
  const [respostaPersonalizada, setRespostaPersonalizada] = useState("");
  const perguntaRef = useRef(null);
  const respostaPersRef = useRef(null);
  const perguntaSelRef = useRef({ start: 0, end: 0 });
  const respostaPersSelRef = useRef({ start: 0, end: 0 });
  const { data: agentsData, isLoading: isLoadingAgents } = useAgents(1, 200, "");
  const activeAgents = useMemo(
    () => (agentsData?.agents || []).filter((a) => String(a.status || "").toLowerCase() === "active"),
    [agentsData?.agents]
  );

  const selectValue = useMemo(
    () => modeSelectValue(respostaModo, agenteIa),
    [respostaModo, agenteIa]
  );

  useEffect(() => {
    setMode(selectValue);
  }, [selectValue]);

  useEffect(() => {
    const cfg = agenteIa || defaultAgenteIaConfig();
    setPergunta(cfg.pergunta || "");
    setEnvioResposta(cfg.envio_resposta || ENVIO_RESPOSTA_DIRETO);
    setRespostaPersonalizada(cfg.resposta_personalizada || "");
  }, [slotKey, agenteIa?.agent_id, agenteIa?.pergunta, agenteIa?.envio_resposta, agenteIa?.resposta_personalizada]);
  const [urls, setUrls] = useState([]);
  const [docs, setDocs] = useState([]);
  const [faqFocus, setFaqFocus] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });
  const fileInputRef = useRef(null);
  const documentIdRef = useRef(0);
  const { uploadMany: uploadManyDocs } = useS3Upload();

  const isAgentMode = String(mode || "").startsWith(MODE_AGENT_PREFIX);

  const emitAgentChange = (patch) => {
    onAgenteIaChange?.({
      agent_id: agenteIa?.agent_id || "",
      pergunta,
      envio_resposta: envioResposta,
      resposta_personalizada: respostaPersonalizada,
      ...patch,
    });
  };

  const handleInsertPerguntaVar = (varName) => {
    const el = perguntaRef.current;
    const { start, end } = perguntaSelRef.current;
    const before = pergunta.slice(0, start);
    const after = pergunta.slice(end);
    const next = `${before}${varName}${after}`;
    setPergunta(next);
    emitAgentChange({ pergunta: next });
    const pos = before.length + varName.length;
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(pos, pos);
      }
      perguntaSelRef.current = { start: pos, end: pos };
    });
  };

  const handleInsertRespostaPersVar = (varName) => {
    const el = respostaPersRef.current;
    const { start, end } = respostaPersSelRef.current;
    const before = respostaPersonalizada.slice(0, start);
    const after = respostaPersonalizada.slice(end);
    const next = `${before}${varName}${after}`;
    setRespostaPersonalizada(next);
    emitAgentChange({ resposta_personalizada: next });
    const pos = before.length + varName.length;
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(pos, pos);
      }
      respostaPersSelRef.current = { start: pos, end: pos };
    });
  };

  const agentExtraVars = useMemo(
    () => [{ name: "$respostaagente", label: "Resposta do agente de IA", group: "agente" }],
    []
  );

  const persistedKey = useMemo(
    () =>
      JSON.stringify({
        slotKey,
        documents: (faqIa?.documents || []).map((d) => ({
          url: d.url || d.file_url || "",
          file_name: d.file_name || d.name || "",
        })),
        source_urls: faqIa?.source_urls || [],
        faq_answer_focus: faqIa?.faq_answer_focus || "",
      }),
    [slotKey, faqIa?.documents, faqIa?.source_urls, faqIa?.faq_answer_focus]
  );

  useEffect(() => {
    const cfg = faqIa || defaultFaqIaConfig();
    setUrls(Array.isArray(cfg.source_urls) ? [...cfg.source_urls] : []);
    setFaqFocus(cfg.faq_answer_focus || "");
    setDocs((prev) => {
      const pending = prev.filter((d) => d.file);
      const persisted = (Array.isArray(cfg.documents) ? cfg.documents : []).map((d, i) => ({
        id: d.id || `persisted_${slotKey}_${i}`,
        name: d.file_name || d.name || "documento",
        url: d.url || d.file_url,
        file: null,
      }));
      if (!pending.length) return persisted;
      const pendingNames = new Set(pending.map((d) => d.name));
      return [...persisted.filter((d) => !pendingNames.has(d.name)), ...pending];
    });
  }, [persistedKey, faqIa, slotKey]);

  useEffect(() => {
    const onAddFiles = (e) => handleAddFiles(e.detail || []);
    window.addEventListener("agent-documents:addFiles", onAddFiles);
    return () => window.removeEventListener("agent-documents:addFiles", onAddFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, urls, faqFocus]);

  const emitFaqChange = (nextUrls, nextDocs, nextFocus) => {
    onFaqIaChange?.({
      source_urls: nextUrls,
      documents: nextDocs
        .filter((d) => d.url && !d.file)
        .map((d) => ({ url: d.url, file_name: d.name })),
      faq_answer_focus: nextFocus,
      system_prompt: faqIa?.system_prompt || "",
      model: faqIa?.model || "",
      temperature: faqIa?.temperature ?? null,
      pendingFiles: nextDocs.filter((d) => d.file),
    });
  };

  const handleModeChange = (nextValue) => {
    setMode(nextValue);
    if (String(nextValue).startsWith(MODE_AGENT_PREFIX)) {
      const agentId = String(nextValue).slice(MODE_AGENT_PREFIX.length);
      onRespostaModoChange?.(RESPOSTA_MODO_AGENTE_IA);
      onAgenteIaChange?.({
        ...(agenteIa || defaultAgenteIaConfig()),
        agent_id: agentId,
      });
      return;
    }
    onRespostaModoChange?.(nextValue);
  };

  const [newUrl, setNewUrl] = useState("");

  const addUrl = () => {
    const u = String(newUrl || "").trim();
    if (!u) return;
    if (urls.length >= MAX_CHATBOT_RAG_URLS) {
      setSnack({ open: true, severity: "warning", message: t("chatbotConfigPage.chatbotSlotFaq.maxUrls", { defaultValue: `Máximo de ${MAX_CHATBOT_RAG_URLS} URLs.` }) });
      return;
    }
    const next = [...urls, u];
    setUrls(next);
    setNewUrl("");
    emitFaqChange(next, docs, faqFocus);
  };

  const removeUrl = (idx) => {
    const next = urls.filter((_, i) => i !== idx);
    setUrls(next);
    emitFaqChange(next, docs, faqFocus);
  };

  const handleAddFiles = (files) => {
    const list = Array.from(files || []);
    const available = Math.max(0, MAX_CHATBOT_RAG_DOCS - docs.length);
    const slice = list.slice(0, available);
    if (!slice.length) {
      setSnack({ open: true, severity: "warning", message: t("chatbotConfigPage.chatbotSlotFaq.maxDocs", { defaultValue: `Máximo de ${MAX_CHATBOT_RAG_DOCS} documentos.` }) });
      return;
    }
    const next = [
      ...docs,
      ...slice.map((file) => ({
        id: `new_${documentIdRef.current++}`,
        name: file.name,
        url: "",
        file,
      })),
    ];
    setDocs(next);
    emitFaqChange(urls, next, faqFocus);
  };

  useImperativeHandle(ref, () => ({
    async prepareForSave() {
      const pending = docs.filter((d) => d.file);
      if (!pending.length) {
        return {
          source_urls: urls,
          documents: docs.filter((d) => d.url && !d.file).map((d) => ({ url: d.url, file_name: d.name })),
          faq_answer_focus: faqFocus,
          system_prompt: faqIa?.system_prompt || "",
          model: faqIa?.model || "",
          temperature: faqIa?.temperature ?? null,
        };
      }
      const prefix = buildDocumentsPrefix(chatbotUserId);
      const uploaded = await uploadManyDocs(
        pending.map((d) => d.file),
        { prefix }
      );
      let upIdx = 0;
      const mergedDocs = docs.map((d) => {
        if (!d.file) return d;
        const url = uploaded[upIdx]?.url || "";
        upIdx += 1;
        return { ...d, url, file: null };
      });
      setDocs(mergedDocs);
      return {
        source_urls: urls,
        documents: mergedDocs.filter((d) => d.url).map((d) => ({ url: d.url, file_name: d.name })),
        faq_answer_focus: faqFocus,
        system_prompt: faqIa?.system_prompt || "",
        model: faqIa?.model || "",
        temperature: faqIa?.temperature ?? null,
      };
    },
  }));

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
        {t("chatbotConfigPage.chatbotSlotFaq.sectionTitle", { defaultValue: "Modo de resposta" })}
      </Typography>
      {isExcecao && (
        <Alert severity="info" sx={{ mt: 1, mb: 1.5 }}>
          {t("chatbotConfigPage.chatbotSlotFaq.exceptionHint", {
            defaultValue:
              "Com Agente FAQ ativo, quando nenhuma opção do fluxo for válida o bot responde com base na base de conhecimento — igual à skill FAQ dos agentes de IA.",
          })}
        </Alert>
      )}
      <FormControl size="small" fullWidth sx={{ mt: 1, mb: 2 }}>
        <InputLabel>{t("chatbotConfigPage.chatbotSlotFaq.modeLabel", { defaultValue: "Como responder" })}</InputLabel>
        <Select
          label={t("chatbotConfigPage.chatbotSlotFaq.modeLabel", { defaultValue: "Como responder" })}
          value={mode}
          onChange={(e) => handleModeChange(e.target.value)}
          disabled={readOnly}
        >
          <MenuItem value={RESPOSTA_MODO_TEXTO}>
            {t("chatbotConfigPage.chatbotSlotFaq.modeText", { defaultValue: "Mensagem fixa (texto/botões)" })}
          </MenuItem>
          <MenuItem value={RESPOSTA_MODO_FAQ_IA}>
            {t("chatbotConfigPage.chatbotSlotFaq.modeFaq", { defaultValue: "Agente FAQ (IA + base de conhecimento)" })}
          </MenuItem>
          {activeAgents.length > 0 && <Divider />}
          {activeAgents.map((agent) => (
            <MenuItem key={agent.id} value={`${MODE_AGENT_PREFIX}${agent.id}`}>
              {`Agente de IA: ${agent.name || agent.id}`}
            </MenuItem>
          ))}
          {!isLoadingAgents && activeAgents.length === 0 && (
            <MenuItem disabled value="__no_agents__">
              {t("chatbotConfigPage.chatbotSlotFaq.noAgents", { defaultValue: "Nenhum agente de IA ativo" })}
            </MenuItem>
          )}
        </Select>
      </FormControl>

      {isAgentMode && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <SmartToyOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">
              {t("chatbotConfigPage.chatbotSlotFaq.agentTitle", { defaultValue: "Consulta ao agente de IA" })}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {t("chatbotConfigPage.chatbotSlotFaq.agentIntro", {
              defaultValue:
                "Ao chegar neste passo, o chatbot envia a pergunta abaixo ao agente selecionado e usa a resposta para responder o cliente.",
            })}
          </Typography>

          <ContextVariableChips onInsert={handleInsertPerguntaVar} t={t} />

          <TextField
            label={t("chatbotConfigPage.chatbotSlotFaq.agentQuestionLabel", { defaultValue: "Pergunta para o agente" })}
            size="small"
            fullWidth
            multiline
            rows={3}
            value={pergunta}
            onChange={(e) => {
              const v = e.target.value;
              setPergunta(v);
              emitAgentChange({ pergunta: v });
            }}
            onBlur={(e) => {
              perguntaSelRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
            }}
            inputRef={perguntaRef}
            disabled={readOnly}
            placeholder={t("chatbotConfigPage.chatbotSlotFaq.agentQuestionPlaceholder", {
              defaultValue: "Ex.: Com base no histórico, qual o status do pedido de $nomecliente? Última mensagem: $mensagem",
            })}
            helperText={t("chatbotConfigPage.chatbotSlotFaq.agentQuestionHelper", {
              defaultValue: "Use variáveis de contexto ($nomecliente, $mensagem, etc.). Se vazio, usa a última mensagem do usuário.",
            })}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t("chatbotConfigPage.chatbotSlotFaq.agentDeliveryTitle", { defaultValue: "Como enviar a resposta ao cliente" })}
          </Typography>
          <RadioGroup
            value={envioResposta}
            onChange={(e) => {
              const v = e.target.value;
              setEnvioResposta(v);
              emitAgentChange({ envio_resposta: v });
            }}
          >
            <FormControlLabel
              value={ENVIO_RESPOSTA_DIRETO}
              control={<Radio size="small" disabled={readOnly} />}
              label={t("chatbotConfigPage.chatbotSlotFaq.agentDeliveryDirect", {
                defaultValue: "Enviar resposta do agente diretamente",
              })}
            />
            <FormControlLabel
              value={ENVIO_RESPOSTA_PERSONALIZADO}
              control={<Radio size="small" disabled={readOnly} />}
              label={t("chatbotConfigPage.chatbotSlotFaq.agentDeliveryCustom", {
                defaultValue: "Mensagem personalizada (com variáveis + $respostaagente)",
              })}
            />
          </RadioGroup>

          {envioResposta === ENVIO_RESPOSTA_PERSONALIZADO && (
            <>
              <ContextVariableChips onInsert={handleInsertRespostaPersVar} extraVars={agentExtraVars} t={t} />
              <TextField
                label={t("chatbotConfigPage.chatbotSlotFaq.agentCustomLabel", { defaultValue: "Mensagem personalizada" })}
                size="small"
                fullWidth
                multiline
                rows={4}
                value={respostaPersonalizada}
                onChange={(e) => {
                  const v = e.target.value;
                  setRespostaPersonalizada(v);
                  emitAgentChange({ resposta_personalizada: v });
                }}
                onBlur={(e) => {
                  respostaPersSelRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
                }}
                inputRef={respostaPersRef}
                disabled={readOnly}
                placeholder={t("chatbotConfigPage.chatbotSlotFaq.agentCustomPlaceholder", {
                  defaultValue: "Olá $nomecliente! Segue a informação:\n\n$respostaagente",
                })}
                sx={{ mt: 1 }}
              />
            </>
          )}

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            {t("chatbotConfigPage.chatbotSlotFaq.agentFallbackHint", {
              defaultValue:
                'A "Resposta" acima serve como fallback se o agente não responder ou estiver inativo.',
            })}
          </Typography>
        </Paper>
      )}

      {mode === RESPOSTA_MODO_FAQ_IA && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <MenuBookOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">
              {t("chatbotConfigPage.chatbotSlotFaq.knowledgeTitle", { defaultValue: "Base de conhecimento (RAG)" })}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {t("chatbotConfigPage.chatbotSlotFaq.knowledgeIntro", {
              defaultValue:
                "Documentos e links usados para responder — mesmo funcionamento da skill FAQ dos agentes de IA. Opcional, mas recomendado.",
            })}
          </Typography>

          <AgentDocumentsSection
            documents={docs}
            fileInputRef={fileInputRef}
            handleClickAdd={() => fileInputRef.current?.click()}
            handleDrop={(e) => {
              e.preventDefault();
              handleAddFiles(e.dataTransfer?.files);
            }}
            handleDragOver={(e) => e.preventDefault()}
            moveDocument={(from, to) => {
              const next = [...docs];
              const [item] = next.splice(from, 1);
              next.splice(to, 0, item);
              setDocs(next);
              emitFaqChange(urls, next, faqFocus);
            }}
            removeDocument={(idx) => {
              const next = docs.filter((_, i) => i !== idx);
              setDocs(next);
              emitFaqChange(urls, next, faqFocus);
            }}
            readOnly={readOnly}
            accept={ACCEPT_DOCS}
            embedded
          />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2.5, mb: 1 }}>
            {t("chatbotConfigPage.chatbotSlotFaq.urlsTitle", { defaultValue: "Links do site" })}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="https://..."
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              disabled={readOnly}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addUrl();
                }
              }}
            />
            <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addUrl} disabled={readOnly}>
              {t("chatbotConfigPage.chatbotSlotFaq.addUrl", { defaultValue: "Adicionar" })}
            </Button>
          </Stack>
          {urls.length > 0 && (
            <List dense disablePadding>
              {urls.map((u, idx) => (
                <ListItem key={`${u}-${idx}`} disableGutters sx={{ py: 0.25 }}>
                  <LinkIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                  <ListItemText primary={u} primaryTypographyProps={{ variant: "body2", sx: { wordBreak: "break-all" } }} />
                  {!readOnly && (
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small" onClick={() => removeUrl(idx)} aria-label="Remover URL">
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          )}

          <TextField
            label={t("chatbotConfigPage.chatbotSlotFaq.focusLabel", { defaultValue: "Foco do FAQ (instruções ao modelo)" })}
            size="small"
            fullWidth
            multiline
            rows={2}
            value={faqFocus}
            onChange={(e) => {
              const v = e.target.value;
              setFaqFocus(v);
              emitFaqChange(urls, docs, v);
            }}
            disabled={readOnly}
            helperText={t("chatbotConfigPage.chatbotSlotFaq.focusHelper", {
              defaultValue: "Ex.: priorizar políticas de cancelamento e documentos aceitos.",
            })}
            sx={{ mt: 2 }}
          />

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            {t("chatbotConfigPage.chatbotSlotFaq.fallbackHint", {
              defaultValue:
                'A "Resposta" acima serve como fallback se a IA não conseguir responder. Configure botões de feedback (ex.: "A resposta ajudou?" / "Falar com atendente").',
            })}
          </Typography>
        </Paper>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
});

export default ChatbotSlotFaqSection;
